import {
  Public as PublicIcon,
  Gavel as GavelIcon,
  Factory as FactoryIcon,
  Shield as ShieldIcon,
  Balance as BalanceIcon,
} from '@mui/icons-material';
import InfoIcon from "@/assets/svgs/info.svg";
import { format, parse } from 'date-fns';
import { DutyRuleInput, RateType, ValueBasis, ExclusiveMode, AUTHORITIES as PC_AUTHORITIES } from '@/services/product-catalogue/duty-columns-api';

export interface FormRule extends Omit<DutyRuleInput, 'rate'> {
  _id?: string;
  rate: string;
  isExpanded?: boolean;
  showAdvanced?: boolean;
  nonMetalAllowedStacksWith?: string[];
}

export const AUTHORITIES = PC_AUTHORITIES;

export { RateType, ValueBasis, ExclusiveMode };

// Helper function to get authority icon and color
export const getAuthorityStyle = (authority?: string): { icon: JSX.Element; color: string } => {
  switch (authority) {
    case 'MFN':
      return { icon: <PublicIcon fontSize="small" />, color: '#3b82f6' };
    case 'SECTION_301':
      return { icon: <GavelIcon fontSize="small" />, color: '#f59e0b' };
    case 'SECTION_232':
      return { icon: <FactoryIcon fontSize="small" />, color: '#ef4444' };
    case 'SECTION_201':
      return { icon: <ShieldIcon fontSize="small" />, color: '#8b5cf6' };
    case 'IEEPA-Reciprocal':
      return { icon: <ShieldIcon fontSize="small" />, color: '#ec4899' };
    case 'IEEPA-Fent':
      return { icon: <ShieldIcon fontSize="small" />, color: '#f43f5e' };
    case 'IEEPA-Russian-Oil':
      return { icon: <ShieldIcon fontSize="small" />, color: '#0ea5e9' };
    case 'AD':
    case 'CVD':
      return { icon: <BalanceIcon fontSize="small" />, color: '#10b981' };
    default:
      return { icon: <img src={InfoIcon} alt="Info" style={{ width: 20, height: 20 }} />, color: '#6b7280' };
  }
};

// Generate a summary for collapsed rules
export const getRuleSummary = (rule: FormRule): string => {
  // Ensure date-only strings (YYYY-MM-DD) are interpreted in local time,
  // avoiding UTC parsing which can shift the displayed day back in negative offsets.
  const toDisplayDate = (dateString: string): string => {
    const isYmd = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    const d = isYmd ? parse(dateString, 'yyyy-MM-dd', new Date()) : new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return format(d, 'MMM d, yyyy');
  };

  const parts: string[] = [];
  
  if (rule.rate) {
    parts.push(`${rule.rate}%`);
  }
  
  if (rule.countries.length > 0) {
    const countryStr = rule.countries.length === 1 
      ? rule.countries[0] 
      : `${rule.countries.length} countries`;
    parts.push(countryStr);
  }
  
  if (rule.htsCodes.length > 0) {
    const htsStr = rule.htsCodes.length === 1
      ? `HTS ${rule.htsCodes[0]}`
      : `${rule.htsCodes.length} HTS codes`;
    parts.push(htsStr);
  }
  
  if (rule.startDate) {
    const startDate = toDisplayDate(rule.startDate as any);
    const endDate = rule.endDate ? toDisplayDate(rule.endDate as any) : 'ongoing';
    parts.push(`${startDate} - ${endDate}`);
  }
  
  return parts.join(' • ') || 'No configuration';
}; 